<template>
  <div class="container">
    <header class="d-flex flex-wrap justify-content-between align-items-center py-3 mb-4 border-bottom sticky-top">
      <router-link to="/" class="navbar-brand d-flex align-items-center">
        <img src="/logo_28x28.png" alt="Logo" class="me-2" width="28" height="28">
        <span class="fs-4" v-if="isTranslationsLoaded">{{ tg('session', 'title') }}</span>
        <SkeletonText v-else width="120px" height="1.5rem" />
      </router-link>
      <ul class="nav nav-pills mt-1 mt-md-0" v-if="wsConnected && isSessionInit">
        <li class="nav-item">
          <router-link to="/admin" class="btn btn-outline-info border-0" v-if="user?.admin">
            <i class="bi bi-gear-fill"></i>
          </router-link>
        </li>
        <li class="nav-item dropdown" id="dropdownLanguageSwitcher" v-if="languageSwitcherDoNotShow === false">
          <button class="btn btn-outline-info dropdown-toggle border-0" type="button" id="languageDropdown" data-bs-toggle="dropdown" aria-expanded="false">
            <i class="bi bi-translate"></i>
          </button>
          <ul class="dropdown-menu overflow-auto" style="max-height: 300px;" aria-labelledby="languageDropdown">
            <li v-for="language in recommendedLanguages" :key="language.idLanguage || language.languageCode">
              <button
                  class="dropdown-item w-100 text-start d-flex align-items-center"
                  :class="{
                    'active bg-info text-white': language.idLanguage === idLanguage,
                  }"
                  @click="selectLanguage(language.idLanguage, language.idLocale)"
                  type="button"
              >
                <img
                    v-if="language.countryCode"
                    :src="'/images/country/' + language.countryCode + '/20x16.png'"
                    :alt="language.countryCode + ' flag'"
                    class="flag-icon me-2"
                    style="width: 20px; height: 16px;"
                >
                <span v-else class="me-2" style="width: 20px; display: inline-block;"></span>
                {{ language.selfTitle }}
              </button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <button 
                class="dropdown-item w-100 text-start" 
                @click="selectLanguage(null, null)"
                :class="{ 'active bg-info text-white': idLanguage === null }"
                type="button"
              >
                <i class="bi bi-arrow-clockwise me-2"></i>
                Автовыбор языка
              </button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <button class="dropdown-item w-100 text-center" @click="selectLanguageModal" type="button">
                Все языки
              </button>
            </li>
            <li><hr class="dropdown-divider"></li>
            <li>
              <button class="dropdown-item w-100 text-start" @click="hideTopMenuLanguageSwitcher" type="button">
                <i class="bi bi-x-lg"></i> Убрать из меню
              </button>
            </li>
          </ul>
        </li>
        <li class="nav-item dropdown" id="dropdownThemeSwitcher" v-if="themeDoNotShow === false">
          <span class="btn btn-outline-info dropdown-toggle border-0" role="button" data-bs-toggle="dropdown" aria-expanded="false" style="cursor: pointer;">
            <i :class="currentIcon"></i>
          </span>
          <ul class="dropdown-menu" aria-labelledby="themeSwitcher">
            <li><span class="dropdown-item" @click="() => setTopMenuTheme('light')" style="cursor: pointer;"><i class="bi bi-brightness-high"></i> {{ tg('session', 'theme_light') }}</span></li>
            <li><span class="dropdown-item" @click="() => setTopMenuTheme('dark')" style="cursor: pointer;"><i class="bi bi-moon"></i> {{ tg('session', 'theme_dark') }}</span></li>
            <li><span class="dropdown-item" @click="() => setTopMenuTheme('auto')" style="cursor: pointer;"><i class="bi bi-circle-half"></i> {{ tg('session', 'theme_auto') }}</span></li>
            <li><hr class="dropdown-divider"></li>
            <li><span class="dropdown-item" @click="hideTopMenuTheme" style="cursor: pointer;"><i class="bi bi-x-lg"></i> {{ tg('session', 'theme_removeFromMenu') }}</span></li>
          </ul>
        </li>
        <li class="nav-item">
          <router-link to="/profile" class="btn btn-outline-info dropdown-toggle border-0" v-if="!user?.name">
            <i class="bi bi-person"></i>
          </router-link>
        </li>
        <li class="nav-item dropdown" v-if="user?.name" id="dropdownProfile" :data-user-id="user.idUser" :data-user-name="user.name">
          <button class="btn btn-outline-info dropdown-toggle border-0" role="button" data-bs-toggle="dropdown" aria-expanded="false">
            <i class="bi bi-person-circle"></i>
          </button>
          <ul class="dropdown-menu">
            <li>
              <span class="dropdown-item-text fw-bold text-center">{{ user.name }}</span>
            </li>
            <li>
              <hr class="dropdown-divider">
            </li>
            <li>
              <router-link to="/helper" class="dropdown-item">{{ tg('session', 'helper') }}</router-link>
            </li>
            <li>
              <hr class="dropdown-divider">
            </li>
            <li>
              <router-link to="/profile" class="dropdown-item">{{ tg('session', 'profile') }}</router-link>
            </li>
            <li>
              <hr class="dropdown-divider">
            </li>
            <li>
              <a class="dropdown-item" href="javascript:void(0)" @click="actionLogout">{{ tg('session', 'logout') }}</a>
            </li>
          </ul>
        </li>
      </ul>
      <ul class="nav nav-pills mt-1 mt-md-0" v-else>
        <li class="nav-item">
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </li>
      </ul>
    </header>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useRouteAccess } from '@/composables/useRouteAccess';
import { useLanguageModal } from '@/composables/useLanguageModal';
import { useTranslation } from '@/composables/useTranslation';
import { RecommendedLanguage } from '@/store/modules/recommendedLanguages';
import SkeletonText from '@/components/ui/SkeletonText.vue';

const emit = defineEmits(['actionLogout']);
const store = useStore();
const { openModal } = useLanguageModal();
const { tg } = useTranslation();
const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload?: any) => void;

const themes = {
  none: 'bi bi-slash-circle',
  light: 'bi bi-brightness-high',
  dark: 'bi bi-moon',
  auto: 'bi bi-circle-half',
};

const themeInterval = ref<number | null>(null);
const routeAccess = ref(useRouteAccess());

const user = computed(() => store.state.auth.user);
const wsConnected = computed(() => store.state.connection.wsConnected);
const theme = computed(() => store.state.theme.theme);
const themeDoNotShow = computed(() => store.state.theme.themeDoNotShow);
const languageSwitcherDoNotShow = computed(() => store.state.i18n.languageSwitcherDoNotShow);

const recommendedLanguages = computed(() => {
  const languages = store.getters['recommendedLanguages/getUniqueLanguages'];
  return languages.sort((a: RecommendedLanguage, b: RecommendedLanguage) => a.weight < b.weight ? 1 : (a.weight > b.weight ? -1 : 0));
});

const idLanguage = computed(() => store.state.i18n.idLanguage);

const isSessionInit = computed(() => routeAccess.value?.isSessionInit || false);
const currentIcon = computed(() => themes[theme.value ?? 'none']);

// Определяем, загружены ли переводы для session группы
const isTranslationsLoaded = computed(() => {
  return store.state.i18n.translateGroups.session && 
         Object.keys(store.state.i18n.translateGroups.session).length > 0;
});

function applyTheme() {
  if (theme.value === null) {
    document.documentElement.removeAttribute('data-bs-theme');
  } else if (theme.value === 'auto') {
    const autoMode = window.matchMedia('(prefers-color-scheme: light)').matches;
    document.documentElement.setAttribute('data-bs-theme', autoMode ? 'light' : 'dark');
  } else {
    document.documentElement.setAttribute('data-bs-theme', theme.value);
  }
}

function updateThemeIfAuto() {
  if (theme.value === 'auto') {
    applyTheme();
  }
}

function setTopMenuTheme(newTheme: string) {
  store.dispatch('theme/setTheme', newTheme);
  if (user.value.theme !== theme.value && user.value.idUser !== null) {
    wsSend('profileUpdateTheme', {
      newTheme,
    });
  }
}

function selectLanguageModal() {
  openModal();
}

function selectLanguage(idLanguage: number | null, idLocale: number | null) {
  // Если idLanguage null - это автовыбор, передаем null значения
  if (idLanguage === null) {
    wsSend('profileUpdateLocale', {
      idLanguage: null,
      idLocale: null,
    });
    return;
  }

  // В новой структуре данных idLocale уже приходит с языком
  // Если idLocale все еще null, найдем его в recommendedLanguages
  if (idLocale === null) {
    const recommendedLanguage = recommendedLanguages.value.find((lang: RecommendedLanguage) => lang.idLanguage === idLanguage);
    if (recommendedLanguage) {
      idLocale = recommendedLanguage.idLocale;
    }
  }

  wsSend('profileUpdateLocale', {
    idLanguage,
    idLocale,
  });
}

function hideTopMenuLanguageSwitcher() {
  store.dispatch('i18n/setLanguageSwitcherDoNotShow', true);
}

function hideTopMenuTheme() {
  store.dispatch('theme/setThemeDoNotShow', true);
}

function actionLogout() {
  emit('actionLogout');
}

onMounted(() => {
  applyTheme();
  themeInterval.value = window.setInterval(updateThemeIfAuto, 60000);
});

onBeforeUnmount(() => {
  if (themeInterval.value) {
    clearInterval(themeInterval.value);
  }
});

watch(theme, () => {
  applyTheme();
});
</script>

<style scoped>
/* Стили для TopMenu остаются минимальными */
</style>
